import React, {useState, useEffect, useRef} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {
    Tabs,
    Card,
    Typography,
    Button,
    Spin,
    App,
    Row,
    Col,
    Steps,
    Space,
    QRCode, Badge
} from 'antd';
import {
    CopyOutlined,
    PrinterOutlined
} from '@ant-design/icons';
import {authService, cashierService} from '../../services/api';
import {usePageTitle} from '../../context/PageTitleContext';
import ErrorPage from "../ErrorPage/ErrorPage";
import HorizontalViewItem from "../../components/HorizontalItems/ViewItem";
import JsBarcode from 'jsbarcode';


// Функция для форматирования даты
const formatDate = (dateString) => {
    if (!dateString) return 'Не указано';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}.${month}.${year} в ${hours}:${minutes}`;
};

// Функция для форматирования ФИО
const formatFullName = (l_name, f_name, m_name) => {
    const parts = [l_name, f_name, m_name].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : 'Не указано';
};

const {Title, Text} = Typography;

const generateBarcode = (value, elementId) => {
    try {
        JsBarcode(`#${elementId}`, value, {
            format: "CODE128",
            width: 2,
            height: 30,
            displayValue: false,
            fontSize: 10,
            margin: 0
        });
    } catch (error) {
        console.error('Ошибка генерации штрихкода:', error);
    }
};

// Компонент визитки
const BusinessCard = ({ cashier, onCopyQR, onPrint }) => {
    const barcodeRef = useRef(null);

    useEffect(() => {
        if (cashier.customer_card_number && barcodeRef.current) {
            generateBarcode(cashier.customer_card_number, 'barcode-svg');
        }
    }, [cashier.customer_card_number]);

    return (
        <div>
            <Card
                className="business-card"
                size="small"
                style={{
                    width: 350,
                    padding: 16
                }}
                bodyStyle={{
                    padding: 0,
                    width: '100%'
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: cashier.customer_card_number ? 16 : 0 }}>
                    <div className="qr-wrapper">
                        <QRCode
                            value={cashier.uuid || cashier.id.toString()}
                            size={100}
                            level="M"
                            bordered={false}
                            className="qr-code"
                        />
                    </div>
                    <div className="cashier-info" style={{ flex: 1 }}>
                        <Typography.Text strong className="cashier-name" style={{ fontSize: 16, lineHeight: 1.3, marginBottom: 8, display: 'block' }}>
                            {[cashier.l_name, cashier.f_name, cashier.m_name].filter(Boolean).join(' ') || 'Не указано'}
                        </Typography.Text>
                        <Typography.Text className="cashier-role" style={{ fontSize: 14 }}>
                            {cashier.role || 'Кассир'}
                        </Typography.Text>
                    </div>
                </div>

                {cashier.customer_card_number && (
                        <div style={{ textAlign: 'center' }}>
                            <svg
                                ref={barcodeRef}
                                id="barcode-svg"
                            />
                        </div>
                )}
            </Card>

            <Space style={{ marginTop: 16 }}>
                <Button
                    icon={<CopyOutlined />}
                    onClick={onCopyQR}
                    type="default"
                >
                    Скачать QR
                </Button>
                <Button
                    icon={<PrinterOutlined />}
                    onClick={onPrint}
                    type="default"
                >
                    Печать
                </Button>
            </Space>
        </div>
    );
};


const CashierPage = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const {message} = App.useApp();
    const {setPageTitle, setRightContent} = usePageTitle();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [cashier, setCashier] = useState(null);
    const [historyData, setHistoryData] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyLoaded, setHistoryLoaded] = useState(false);

    useEffect(() => {
        fetchData();
        setPageTitle('Просмотр кассира');

        setRightContent(
            authService.hasPermission('pos:cashiers:edit') && (
                <Button
                    type="primary"
                    onClick={() => navigate(`/cashiers/${id}/edit`)}
                >
                    Редактировать
                </Button>
            )
        );

        return () => {
            setPageTitle('');
            setRightContent(null);
            setCashier(null);
        };
    }, [setPageTitle, setRightContent, id, navigate]);

    const fetchData = async () => {
        try {
            setLoading(true);

            const cashierResponse = await cashierService.getCashierById(id);

            if (cashierResponse.status === 'success') {
                setCashier(cashierResponse.data);
                console.log('Получены данные кассира:', cashierResponse.data);
            } else {
                message.error('Не удалось загрузить данные кассира');
                navigate('/cashiers');
            }
        } catch (error) {
            if (error.response?.status === 404) {
                setError({
                    statusCode: 404,
                    title: "Кассир не найден",
                    description: "Указанный кассир не существует или был удален."
                });
            } else {
                setError({
                    statusCode: error.response?.status || 500,
                    title: "Ошибка загрузки данных",
                    description: "Произошла ошибка при загрузке данных кассира. Попробуйте обновить страницу или вернуться позже."
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchHistory = async () => {
        try {
            setHistoryLoading(true);
            const response = await cashierService.getCashierHistory(id);

            if (response.status === 'success') {
                setHistoryData(response.data);
            } else {
                message.error('Не удалось загрузить историю изменений');
            }
        } catch (error) {
            message.error(error.message || 'Ошибка при загрузке истории изменений');
        } finally {
            setHistoryLoading(false);
            setHistoryLoaded(true);
        }
    };

    const handleDownloadQR = () => {
        try {
            const canvas = document.querySelector('.qr-code canvas');
            if (!canvas) {
                message.error('QR код не найден');
                return;
            }

            // Создаем ссылку для скачивания
            const link = document.createElement('a');
            link.download = `qr-cashier-${cashier.id}.png`;
            link.href = canvas.toDataURL('image/png');

            // Добавляем в DOM, кликаем и удаляем
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            message.success('QR код скачан');
        } catch (error) {
            message.error('Не удалось скачать QR код');
        }
    };

    const handlePrintCard = () => {
        const printContent = document.querySelector('.business-card');
        const qrCanvas = document.querySelector('.qr-code canvas');
        const barcodeSvg = document.querySelector('#barcode-svg');

        if (!printContent || !qrCanvas) {
            message.error('Не удалось найти визитку для печати');
            return;
        }

        const qrDataUrl = qrCanvas.toDataURL('image/png');

        const openPrintWindow = (barcodeDataUrl = '') => {

            const printWindow = window.open('', '_blank');
            if (!printWindow) {
                message.error('Не удалось открыть окно печати');
                return;
            }

            printWindow.document.write(`
        <html>
            <head>
                <title>Визитка кассира</title>
                <style>
                    @page {
                        size: 58mm 40mm;
                        margin: 0;
                    }
                    * {
                        box-sizing: border-box;
                    }
                    body {
                        margin: 0;
                        padding: 0;
                        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                        -webkit-print-color-adjust: exact;
                        color-adjust: exact;
                    }
                    .business-card {
                        width: 58mm;
                        height: 40mm;
                        border: 1px solid #000;
                        padding: 3mm;
                        background: white;
                        box-sizing: border-box;
                        display: flex;
                        flex-direction: column;
                    }
                    .card-top {
                        display: flex;
                        align-items: center;
                        gap: 3mm;
                        ${cashier.customer_card_number ? 'flex: 1;' : ''}
                    }
                    .qr-wrapper {
                        flex-shrink: 0;
                        width: 20mm;
                        height: 20mm;
                    }
                    .qr-wrapper img {
                        width: 20mm;
                        height: 20mm;
                        display: block;
                    }
                    .cashier-info {
                        flex: 1;
                        min-width: 0;
                    }
                    .cashier-name {
                        font-size: 3.5mm;
                        font-weight: bold;
                        line-height: 1.2;
                        margin: 0 0 1mm 0;
                        word-wrap: break-word;
                    }
                    .cashier-role {
                        font-size: 3mm;
                        margin: 0;
                    }
                    .barcode-section {
                        border-top: 1px solid #ccc;
                        padding-top: 2mm;
                        margin-top: 2mm;
                        text-align: center;
                    }
                    .barcode-label {
                        font-size: 2.5mm;
                        color: #666;
                        margin-bottom: 1mm;
                    }
                    .barcode-section img {
                        max-width: 100%;
                        height: auto;
                    }
                </style>
            </head>
            <body>
                <div class="business-card">
                    <div class="card-top">
                        <div class="qr-wrapper">
                            <img src="${qrDataUrl}" alt="QR Code" />
                        </div>
                        <div class="cashier-info">
                            <div class="cashier-name">${[cashier.l_name, cashier.f_name, cashier.m_name].filter(Boolean).join(' ') || 'Не указано'}</div>
                            <div class="cashier-role">${cashier.role || 'Кассир'}</div>
                        </div>
                    </div>
                    ${cashier.customer_card_number && barcodeDataUrl ? `
                    <div class="barcode-section">
                        <img src="${barcodeDataUrl}" alt="Barcode" />
                    </div>
                    ` : ''}
                </div>
            </body>
        </html>
        `);
            printWindow.document.close();
            printWindow.focus();
            setTimeout(() => {
                printWindow.print();
                printWindow.close();
            }, 500);
        };

        if (barcodeSvg && cashier.customer_card_number) {
            try {
                const svgData = new XMLSerializer().serializeToString(barcodeSvg);
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                const img = new Image();

                canvas.width = barcodeSvg.getBoundingClientRect().width || 200;
                canvas.height = barcodeSvg.getBoundingClientRect().height || 50;

                const svgBlob = new Blob([svgData], {type: 'image/svg+xml;charset=utf-8'});
                const url = URL.createObjectURL(svgBlob);

                img.onload = () => {
                    try {
                        ctx.drawImage(img, 0, 0);
                        const barcodeDataUrl = canvas.toDataURL('image/png');
                        URL.revokeObjectURL(url);
                        openPrintWindow(barcodeDataUrl);
                    } catch (error) {
                        console.error('Ошибка при конвертации штрихкода:', error);
                        openPrintWindow();
                    }
                };

                img.onerror = () => {
                    URL.revokeObjectURL(url);
                    openPrintWindow();
                };

                img.src = url;
            } catch (error) {
                console.error('Ошибка при обработке штрихкода:', error);
                openPrintWindow();
            }
        } else {
            openPrintWindow();
        }
    };

    if (loading) {
        return (
            <div style={{textAlign: 'center', padding: '50px 0'}}>
                <Spin size="large"/>
            </div>
        );
    }

    if (error) {
        return <ErrorPage
            statusCode={error.statusCode}
            title={error.title}
            description={error.description}
        />;
    }

    return (
        <Card>
            <Title level={3} style={{marginTop: 0}}>
                Кассир «{formatFullName(cashier.l_name, cashier.f_name, cashier.m_name)}»
            </Title>
            <Space>
                <Badge
                    status={cashier.status === 'active' ? 'success' : 'error'}
                    text={cashier.status === 'active' ? 'Активен' : 'Неактивен'}
                />
                <Text>ID: <Text>{cashier.id}</Text></Text>
            </Space>

            <Tabs defaultActiveKey="data" items={[
                {
                    key: "data",
                    label: "Основная информация",
                    children: (
                        <Row gutter={24}>
                            <Col span={12}>
                                <Space direction="vertical" size={16} style={{ marginBottom: 38, display: 'block' }}>
                                    <Title level={5} style={{ marginBottom: 20 }}>Личные данные</Title>

                                    <HorizontalViewItem label="Фамилия">
                                        <Text>{cashier.l_name || 'Не указано'}</Text>
                                    </HorizontalViewItem>

                                    <HorizontalViewItem label="Имя">
                                        <Text>{cashier.f_name || 'Не указано'}</Text>
                                    </HorizontalViewItem>

                                    <HorizontalViewItem label="Отчество">
                                        <Text>{cashier.m_name || 'Не указано'}</Text>
                                    </HorizontalViewItem>

                                    <HorizontalViewItem label="Роль">
                                        <Text>{cashier.role || 'Не указано'}</Text>
                                    </HorizontalViewItem>

                                    <HorizontalViewItem label="Номер карты клиента">
                                        <Text>{cashier.customer_card_number || 'Не указано'}</Text>
                                    </HorizontalViewItem>
                                </Space>

                                <Space direction="vertical" size={16} style={{ marginBottom: 38, display: 'block' }}>
                                    <Title level={5} style={{ marginBottom: 20 }}>Системная информация</Title>

                                    <HorizontalViewItem label="Дата создания">
                                        <Text>{formatDate(cashier.created_at)}</Text>
                                    </HorizontalViewItem>

                                    <HorizontalViewItem label="Дата обновления">
                                        <Text>{formatDate(cashier.updated_at)}</Text>
                                    </HorizontalViewItem>
                                </Space>
                            </Col>
                            <Col span={12}>
                                <Space direction="vertical" size={16} style={{ marginBottom: 38, display: 'block' }}>
                                    <Title level={5} style={{ marginBottom: 20 }}>Визитка</Title>
                                    <BusinessCard
                                        cashier={cashier}
                                        onCopyQR={handleDownloadQR}
                                        onPrint={handlePrintCard}
                                    />
                                </Space>
                            </Col>
                        </Row>
                    )
                },
                {
                    key: "history",
                    label: "История изменений",
                    children: (
                        <Card>
                            {historyLoading ? (
                                <div style={{textAlign: 'center', padding: '50px 0'}}>
                                    <Spin size="large"/>
                                </div>
                            ) : (
                                <Steps
                                    direction="vertical"
                                    size="small"
                                    current={historyData.length}
                                    progressDot
                                    items={historyData.map(item => ({
                                        title: item.change_description,
                                        description: `${item.full_name} (${item.username}), ${formatDate(item.created_at)}`
                                    }))}
                                />
                            )}
                        </Card>
                    )
                }
            ]}
                  onChange={(key) => {
                      if (key === 'history' && !historyLoaded) {
                          fetchHistory();
                      }
                  }}/>
        </Card>
    );
}

export default CashierPage;